/**
 * The connection dialog's draft REVISION — what authorizes a save (round B, Codex review of the lifecycle work).
 *
 * The Test fingerprint (`useConnectionTestOutcome`) decides whether a result may be SHOWN, and deliberately ignores the
 * label, colour and AI permission. A save must not: Save persisted the values its test captured, so an edit of only
 * those fields during the test was silently lost. Every value edit — any field — an engine change, a dismissal and a new
 * draft move the revision; Save and Save anyway capture it before their first await and re-check it right before they
 * persist, so a save writes the values on screen or refuses and asks. A run's own edit (the storage untick) and password
 * hydration go through `programmatic` and move nothing.
 *
 * Round B follow-up B2: each top-level field also has its own revision, bumped by EVERY change of that field
 * (programmatic ones included), so "has anyone touched this field since I set it?" is answered by revision, never by
 * value — a toggle off and on again returns to the same value but is a later edit.
 */
import { useEffect, useMemo, useRef } from 'react';

type Store = { subscribe: (listener: () => void) => () => void };

export function useDraftRevision(store: Store, readValues: () => unknown) {
  const revision = useRef(0);
  const fieldRevisions = useRef(new Map<string, number>());
  const programmaticDepth = useRef(0);
  const read = useRef(readValues);
  read.current = readValues;
  useEffect(() => {
    let last = read.current();
    return store.subscribe(() => {
      const values = read.current();
      if (values === last) return;
      const before = (last ?? {}) as Record<string, unknown>;
      const after = (values ?? {}) as Record<string, unknown>;
      for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
        if (before[key] !== after[key]) fieldRevisions.current.set(key, (fieldRevisions.current.get(key) ?? 0) + 1);
      }
      last = values;
      if (programmaticDepth.current === 0) revision.current += 1;
    });
  }, [store]);
  return useMemo(() => ({
    /** Capture before the first await of a save. */
    capture: () => revision.current,
    /** Nothing moved since `captured`. */
    isCurrent: (captured: number) => revision.current === captured,
    /** How often `field` changed so far (any change, any source) — compare two reads to learn whether it was touched. */
    fieldRevision: (field: string) => fieldRevisions.current.get(field) ?? 0,
    /** A dismissal or a replaced draft: every pending save is void. */
    invalidate: () => { revision.current += 1; },
    /** An edit the dialog makes on its own behalf (not the user's). */
    programmatic: (edit: () => void) => {
      programmaticDepth.current += 1;
      try { edit(); } finally { programmaticDepth.current -= 1; }
    },
  }), []);
}
